import React, { useState, type ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/cn";

/* One figure on a trace's metrics strip: a quiet label, the number in mono, and what it
   means in a tooltip (a tap opens it on devices without hover). `tone` colours the number:
   idle time warns, failed steps are red. */
export function Metric({ label, description, tone, className, children }: {
  label: string;
  description: ReactNode;
  tone?: "warn" | "danger";
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            setOpen(true);
          }}
          className={cn(
            "press shrink-0 inline-flex items-baseline gap-1.5 h-7 px-2 rounded-[7px] whitespace-nowrap cursor-help hover:bg-[var(--accent-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--text-tertiary)]",
            className,
          )}
        >
          <span className="self-center text-[12px] text-[var(--text-tertiary)]">{label}</span>
          <span
            className={cn(
              "self-center font-mono text-[12.5px] tabular-nums",
              tone === "warn" ? "text-[var(--warn-text)]" : tone === "danger" ? "text-[var(--danger)]" : "text-[var(--text-primary)]",
            )}
          >
            {children}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        sideOffset={8}
        collisionPadding={12}
        className="max-w-[min(280px,calc(100vw-24px))] rounded-[10px] border border-[var(--hairline)] bg-[var(--canvas)] px-3 py-2.5 text-[12.5px] leading-[1.5] tracking-[-0.005em] text-[var(--text-secondary)] shadow-menu animate-none data-[state=closed]:animate-none"
      >
        <div className="mb-1 text-[13px] font-medium text-[var(--text-primary)]">{label}</div>
        <div>{description}</div>
      </TooltipContent>
    </Tooltip>
  );
}

/** The hairline between two figures. */
export function MetricRule({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cn("shrink-0 w-px h-3.5 bg-[var(--hairline-strong)]", className)} />;
}
